/**
 * R31 W2 — the S7 close-card "Done-as-commit" model (the dead-button fix).
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 * The R29 X2 per-field Done buttons (email/phone on the CloseScene) were BLUR-ONLY:
 * `previousElementSibling.blur()`. On a kiosk with no on-screen keyboard the blur is invisible — the
 * dev-118 visitor tapped Done and "nothing really happened" (the buttons read as dead). A Done on a
 * form field should COMMIT the field, not just dismiss a keyboard nobody can see.
 *
 * This module is the PURE decision layer for that commit — validation + the advance intent — so the
 * behaviour is unit-testable without a DOM (the kiosk vitest suite has no jsdom; the same discipline
 * as r30/r22 — the component delegates to these helpers and only carries the DOM side-effects).
 *
 * Done becomes a COMMIT:
 *   1. VALIDATE the field (email shape / phone non-empty-if-touched).
 *   2. Valid   → the host shows a brief inline ACK (a tick on the pressable; reduced-motion safe),
 *                BLURS the field (keyboard still dismisses; the R30 focus-guard still releases), and
 *                ADVANCES the flow: email → focus phone (or surface the Send CTA if phone is filled);
 *                phone → Send.
 *   3. Invalid → a gentle inline HINT, never silence.
 *
 * The FIELD-LEVEL semantics live here; the host owns focus/scroll/submit + the ack animation class.
 */

/** The two S7 close-card fields that carry a per-field Done. */
export type CloseField = 'email' | 'phone';

/**
 * What the host should do after a Done commit resolves. The pure decision; the host maps each to a
 * DOM action (see CloseScene):
 *   - 'focus-phone' → email committed, phone still empty → move focus to the phone field.
 *   - 'surface-send' → email committed AND phone already filled → the email leg is done; bring the
 *      Send CTA into view / focus it (nothing left to type before Send).
 *   - 'send' → phone committed (email already valid) → the contact legs are done; submit (Send).
 *   - 'hint' → validation failed → show the inline hint, hold focus, do NOT advance.
 */
export type CommitAdvance = 'focus-phone' | 'surface-send' | 'send' | 'hint';

export type CommitResult = {
  /** True iff the field passed validation (an ack tick may play; the field may blur + advance). */
  valid: boolean;
  /** The advance intent for the host (only meaningful when `valid`; 'hint' when invalid). */
  advance: CommitAdvance;
  /** A gentle, human inline hint to show when invalid (empty string when valid). Never silence. */
  hint: string;
};

/**
 * Email SHAPE validation — deliberately permissive (a single `@` with a non-empty local part and a
 * dotted domain with a ≥2-char TLD). We are NOT trying to RFC-5322-validate; we only want to catch
 * the obvious typo ("meera@", "meera", "meera@marbledecor") so Done never "commits" a plainly-broken
 * address, while never rejecting a real exotic-but-valid one (the send path + the human follow-up are
 * the real backstop — over-strict client validation that blocks a real address is the worse failure).
 * Trims first (kiosk keyboards trail spaces). No unicode-domain handling — out of scope for the fair.
 */
export function isValidEmailShape(email: string): boolean {
  const v = email.trim();
  if (v.length < 3 || v.length > 254) return false;
  // local@domain.tld — one @, no whitespace, a dotted domain, TLD ≥ 2 letters.
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
}

/**
 * Decide the outcome of a Done commit on a close-card field.
 *
 * @param field    the field the Done belongs to.
 * @param value    the current value of THAT field (verbatim; trimmed inside).
 * @param opts     the sibling-field state the advance decision needs:
 *                 - `phoneFilled`  — is the phone field non-empty? (email → surface-send vs focus-phone)
 *                 - `emailValid`   — is the email already a valid shape? (phone → send only if so)
 *
 * Rules:
 *   - EMAIL: empty → hint ("add your email…"); bad shape → hint ("that doesn't look like an email…");
 *     valid → advance to `focus-phone` (phone empty) or `surface-send` (phone already filled).
 *   - PHONE (optional): empty → NOT an error — a Done on an untouched optional phone just moves on
 *     (`send` if email is valid, else `surface-send` to nudge the email); non-empty → commit + `send`
 *     when email is valid, else `surface-send` (can't send yet — email still needed).
 */
export function commitCloseField(
  field: CloseField,
  value: string,
  opts: { phoneFilled: boolean; emailValid: boolean }
): CommitResult {
  const v = value.trim();
  if (field === 'email') {
    if (v === '') {
      return {
        valid: false,
        advance: 'hint',
        hint: 'Add your email so I can send your spec sheet.',
      };
    }
    if (!isValidEmailShape(v)) {
      return {
        valid: false,
        advance: 'hint',
        hint: "That doesn't look like an email yet — check for a typo?",
      };
    }
    // Valid email → advance. If phone is already filled there's nothing left to type before Send.
    return { valid: true, advance: opts.phoneFilled ? 'surface-send' : 'focus-phone', hint: '' };
  }
  // PHONE — optional. An empty phone Done is NOT an error; it's "I'm done, nothing to add here".
  // Committed (or skipped) → send when the email is valid, else surface the Send CTA / email need.
  return { valid: true, advance: opts.emailValid ? 'send' : 'surface-send', hint: '' };
}
